import { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Team from "../components/Team";
import AppointmentModal from "../components/AppointmentModal";

// Import local images
import heroBg from '../assets/pravinn.webp';
import facility1 from '../assets/gallery4.webp';
import facility2 from '../assets/gallery9.webp';
import facility3 from '../assets/gallery10.webp';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { y: 50, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 10
    }
  }
};

const fadeInVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: "easeOut"
    }
  }
};

const About = () => {
  const [isAppointmentOpen, setIsAppointmentOpen] = useState(false);

  const stats = [
    {
      value: "50,000+",
      label: "Patients Treated Annually",
      description: "Consistent growth since our founding"
    },
    {
      value: "98%",
      label: "Patient Satisfaction",
      description: "Highest in the region for 5 consecutive years"
    },
    {
      value: "24/7",
      label: "Emergency Response",
      description: "Average response time under 15 minutes"
    },
    {
      value: "50+",
      label: "Specialty Departments",
      description: "Comprehensive care under one roof"
    }
  ];

  const timeline = [
    {
      year: "2005",
      title: "Foundation",
      description: "Established as a multi-specialty clinic with 3 departments in Mumbai",
      icon: "🏥"
    },
    {
      year: "2012",
      title: "First Expansion",
      description: "Added cardiology and neurology departments, becoming a regional referral center",
      icon: "🚀"
    },
    {
      year: "2016",
      title: "Technology Adoption",
      description: "Implemented electronic health records and telemedicine capabilities",
      icon: "💻"
    },
    {
      year: "2019",
      title: "Award Recognition",
      description: "Received 'Healthcare Excellence Award' for innovative patient care models",
      icon: "🏆"
    },
    {
      year: "2021",
      title: "Pandemic Response",
      description: "Launched dedicated COVID care units and vaccination drive serving 25,000+ patients",
      icon: "💉"
    },
    {
      year: "2023",
      title: "Current Milestones",
      description: "Expanded to 50+ specialties with new surgical wing and rehabilitation center",
      icon: "🌟"
    }
  ];

  return (
    <div className="bg-gray-50 font-sans">
      {/* SEO Optimization */}
      <Helmet>
        <title>About Us | Premier Healthcare Services in India</title>
        <meta name="description" content="Discover our journey of medical excellence with state-of-the-art facilities and dedicated team providing world-class healthcare services." />
        <meta name="keywords" content="hospital about us, healthcare services, medical team, hospital facilities, patient care India" />
        
        <meta property="og:title" content="About Our Premier Healthcare Services" />
        <meta property="og:description" content="Learn about our journey to provide exceptional healthcare services with cutting-edge technology." />
        <meta property="og:image" content={heroBg} />
        <meta property="og:url" content="http://www.thehealing.in//about" />
        <meta property="og:type" content="website" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Our Premier Healthcare Services" />
        <meta name="twitter:description" content="Learn about our journey to provide exceptional healthcare services with cutting-edge technology." />
        <meta name="twitter:image" content={heroBg} />
      </Helmet>

      {/* Appointment Modal */}
      <AppointmentModal
        isOpen={isAppointmentOpen}
        onClose={() => setIsAppointmentOpen(false)}
      />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={heroBg}
            alt="Healthcare professionals in modern hospital"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>

        <motion.div
          className="text-center px-4 max-w-5xl mx-auto relative z-10"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.8,
            delay: 0.2,
            ease: [0.2, 0.71, 0.4, 1] 
          }}
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-6 font-serif leading-tight text-white">
            Redefining Healthcare Excellence in India
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-gray-100">
            Where world-class medical expertise meets personalized, compassionate care
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <motion.button
              onClick={() => setIsAppointmentOpen(true)}
              className="bg-white hover:bg-gray-100 text-blue-800 font-bold py-3 px-8 rounded-lg transition-all duration-300 shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              aria-label="Book a consultation"
            >
              Book Consultation
            </motion.button>
          </div>
        </motion.div>

        <motion.div
  className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center z-10"
  animate={{ y: [0, 15, 0] }}
  transition={{ 
    repeat: Infinity, 
    duration: 2,
    ease: "easeInOut"
  }}
>
  <span className="text-white mb-2 block">Scroll to Explore</span>
  <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
    <motion.div
      animate={{ y: [0, 6, 0] }}
      transition={{
        repeat: Infinity,
        duration: 2,
        ease: "easeInOut"
      }}
      className="w-1 h-2 bg-white mt-2 rounded-full"
    ></motion.div>
  </div>
</motion.div>

      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 bg-white">
        <motion.div
          className="max-w-6xl mx-auto text-center mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInVariants}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2 font-serif">Our Commitment in Numbers</h2>
          <p className="text-lg text-gray-600">Quantifying our impact on patient care and community health</p>
          <motion.div 
            className="w-20 h-1 bg-blue-600 mx-auto mt-4"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          ></motion.div>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="group relative bg-gray-50 p-8 rounded-xl border border-gray-200 text-center overflow-hidden"
              variants={itemVariants}
              whileHover={{
                y: -10,
                boxShadow: "0 15px 30px rgba(59, 130, 246, 0.15)"
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <motion.div
                  className="text-5xl font-bold text-blue-600 mb-2"
                  whileHover={{ scale: 1.1 }}
                >
                  {stat.value}
                </motion.div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{stat.label}</h3>
                <p className="text-gray-600">{stat.description}</p>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 px-4 bg-gray-50">
        <motion.div
          className="max-w-6xl mx-auto text-center mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInVariants}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2 font-serif">Our Evolution</h2>
          <p className="text-lg text-gray-600">A journey of medical innovation and service excellence</p>
          <motion.div 
            className="w-20 h-1 bg-blue-600 mx-auto mt-4"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          ></motion.div>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Timeline line */}
            <motion.div
              className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-blue-400 to-blue-600 rounded-full"
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2 }}
            ></motion.div>

            {timeline.map((item, index) => (
              <motion.div
                key={index}
                className={`relative mb-12 ${index % 2 === 0 ? 'pr-8 md:pr-0 md:pl-8' : 'pl-8 md:pl-0 md:pr-8'}`}
                initial={{ opacity: 0, x: index % 2 === 0 ? -80 : 80 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ 
                  duration: 0.6,
                  delay: index * 0.15,
                  ease: [0.16, 0.77, 0.47, 0.97] 
                }}
                viewport={{ once: true, margin: "0px 0px -30% 0px" }}
              >
                <div className={`flex flex-col md:flex-row ${index % 2 === 0 ? 'md:flex-row-reverse' : ''} items-center`}>
                  <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pl-8' : 'md:pr-8'}`}>
                    <motion.div
                      className="p-6 bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300 group"
                      whileHover={{
                        background: 'linear-gradient(135deg, rgba(59,130,246,0.05) 0%, rgba(99,102,241,0.05) 100%)',
                        borderColor: 'rgba(99,102,241,0.3)'
                      }}
                    >
                      <div className="text-3xl mb-3 group-hover:text-blue-600 transition-colors">{item.icon}</div>
                      <h3 className="text-xl font-bold text-gray-800 mb-1 group-hover:text-blue-700 transition-colors">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </motion.div>
                  </div>
                  <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:text-right md:pr-8' : 'md:text-left md:pl-8'}`}>
                    <motion.div
                      className="inline-block bg-blue-600 text-white rounded-lg px-6 py-2 font-bold shadow-md"
                      whileHover={{ scale: 1.05 }}
                    >
                      {item.year}
                    </motion.div>
                  </div>
                </div>
                {/* Timeline dot */}
                <div className={`absolute top-1/2 transform -translate-y-1/2 ${index % 2 === 0 ? 'right-0 md:right-auto md:left-0' : 'left-0 md:left-auto md:right-0'}`}>
                  <motion.div
                    className="w-6 h-6 rounded-full bg-blue-600 border-4 border-white shadow-md group-hover:bg-blue-700 transition-colors"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ 
                      delay: index * 0.15 + 0.3,
                      type: "spring",
                      stiffness: 200
                    }}
                  ></motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <Team />

      {/* Gallery Section */}
      <section className="py-20 px-4 bg-gray-50">
        <motion.div
          className="max-w-6xl mx-auto text-center mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInVariants}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2 font-serif">Our Healing Environment</h2>
          <p className="text-lg text-gray-600">State-of-the-art facilities designed for comfort and care</p>
          <motion.div 
            className="w-20 h-1 bg-blue-600 mx-auto mt-4"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          ></motion.div>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {[facility1, facility2, facility3].map((img, index) => (
            <motion.div
              key={index}
              className="overflow-hidden rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-300 group"
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
            >
              <div className="h-64 overflow-hidden relative">
                <motion.img
                  src={img}
                  alt={`Healthcare facility ${index === 0 ? "Advanced Surgical Suite" : index === 1 ? "Rehabilitation Center" : "Diagnostic Imaging Department"}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                  initial={{ opacity: 0.8 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div className="bg-white p-4 text-center">
                <p className="text-gray-700 font-medium group-hover:text-blue-600 transition-colors">
                  {index === 0 ? "Advanced Surgical Suite" :
                    index === 1 ? "Rehabilitation Center" :
                      "Diagnostic Imaging Department"}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* CTA Section */}
      <motion.section 
        className="py-20 px-4 bg-gradient-to-r from-blue-600 to-blue-800"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInVariants}
      >
        <div className="max-w-4xl mx-auto text-center text-white">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold mb-6 font-serif leading-tight"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Begin Your Journey to Better Health
          </motion.h2>
          <motion.p 
            className="text-xl mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            Our patient care coordinators are available 24/7 to assist you
          </motion.p>
          <motion.div 
            className="flex flex-col sm:flex-row justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <motion.button
              onClick={() => setIsAppointmentOpen(true)}
              className="bg-white hover:bg-gray-100 text-blue-800 font-bold py-3 px-8 rounded-lg transition-all duration-300 shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              aria-label="Schedule a consultation"
            >
              Schedule Consultation
            </motion.button>

            <motion.a
              href="tel:+919430003381"
              className="bg-transparent hover:bg-white hover:text-blue-800 text-white font-bold py-3 px-8 border-2 border-white rounded-lg transition-all duration-300 shadow-lg inline-block text-center"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              aria-label="Call emergency number"
            >
              Emergency: +91 9430003381
            </motion.a>
          </motion.div>
          <motion.p 
            className="mt-6 text-blue-100"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
          >
            Or visit us at: Wazirganj Gaya Bihar India - 803051
          </motion.p>
        </div>
      </motion.section>
    </div>
  );
};

export default About;